// M004-F0698 / F0699 (WebUI #102, remaining half): learn the SETTLED door
// outcome of the membership commands this page just sent.
//
// RealmAuthority acks `applied` before its asynchronous door write, so the
// ack carries doorWriteStatus:null. Core records RA's later
// door-write-outcome event and serves it from
// getRealmAuthorityMembershipDoorOutcomes (editor contract 1.2.0). This hook
// polls that read for the commandIds the editor kept from the ack, with a
// bounded backoff, and hands back ONLY results that belong to a tracked
// command by exact commandId (normalisers.matchDoorOutcome) and carry a
// settled status. It never invents a result: no response, a failed request,
// an expired window and an outcome for any other command all leave a row
// exactly as it was ("not confirmed yet").
//
// Schedule: 3s, 6s, 12s, then 12s repeating, counted from the batch
// response; the window closes at 120s. No request is made while the page is
// hidden; becoming visible again polls at once if the window is still open.
// Rationale is in docs/design/M004-F0669-F0671-...md (M004-F0698).
import { useCallback, useEffect, useRef, useState } from 'react';
import { realmAuthorityGroupsApi } from '../../../services/realmAuthorityGroupsApi';
import { matchDoorOutcome, projectMembershipDoorOutcomes } from './normalisers';

export const DOOR_POLL_SCHEDULE = Object.freeze({
  delaysMs: Object.freeze([3000, 6000, 12000]),
  windowMs: 120000,
});

// Nothing changes by asking again: not allowed, not signed in, not linked.
const TERMINAL_STATUSES = new Set([401, 403, 404]);

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * @param organisationId
 * @param groupSid
 * @param tracked  applied outcomes (with commandId) whose ack door status is null
 * @param schedule optional {delaysMs, windowMs} override (tests only)
 * @returns {{results: Map<string, object>, status: 'idle'|'polling'|'settled'|'expired',
 *            checkAgain: () => void, checking: boolean, checkFailed: boolean}}
 */
export default function useDoorOutcomePoll({
  organisationId, groupSid, tracked, schedule = DOOR_POLL_SCHEDULE,
}) {
  const [results, setResults] = useState(() => new Map());
  const [status, setStatus] = useState('idle');
  const [checking, setChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);
  const trackedRef = useRef(tracked);
  trackedRef.current = tracked;
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  const manualRef = useRef(null);
  const key = (tracked || []).map((entry) => entry.commandId).join(',');

  useEffect(() => {
    const acks = trackedRef.current || [];
    setResults(new Map());
    setCheckFailed(false);
    setChecking(false);
    manualRef.current = null;
    if (!acks.length || !organisationId || !groupSid) {
      setStatus('idle');
      return undefined;
    }
    const { delaysMs, windowMs } = scheduleRef.current;
    const unresolved = new Set(acks.map((ack) => ack.commandId));
    const startedAt = Date.now();
    let cancelled = false;
    let open = true;
    let inflight = false;
    let attempt = 0;
    let timer = null;
    let controller = null;
    setStatus('polling');

    const elapsed = () => Date.now() - startedAt;
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const finish = (next) => {
      open = false;
      clear();
      if (!cancelled) setStatus(next);
    };

    // One request. Resolves true when the request itself succeeded.
    const request = async () => {
      inflight = true;
      controller = typeof AbortController === 'function' ? new AbortController() : null;
      try {
        const response = await realmAuthorityGroupsApi.getMembershipOutcomes(
          organisationId, groupSid, { signal: controller?.signal },
        );
        if (cancelled) return { ok: false, terminal: false };
        const projected = projectMembershipDoorOutcomes(response, groupSid);
        if (projected) {
          const settled = [];
          for (const ack of acks) {
            if (!unresolved.has(ack.commandId)) continue;
            const match = matchDoorOutcome(ack, projected.outcomes);
            if (match && match.doorWriteStatus !== null) settled.push(match);
          }
          if (settled.length) {
            for (const entry of settled) unresolved.delete(entry.commandId);
            setResults((current) => {
              const next = new Map(current);
              for (const entry of settled) next.set(entry.commandId, entry);
              return next;
            });
          }
        }
        return { ok: true, terminal: false };
      } catch (error) {
        if (cancelled) return { ok: false, terminal: false };
        return { ok: false, terminal: TERMINAL_STATUSES.has(error?.response?.status) };
      } finally {
        inflight = false;
      }
    };

    const planNext = () => {
      if (!open || cancelled) return;
      const delay = delaysMs[Math.min(attempt, delaysMs.length - 1)];
      const remaining = windowMs - elapsed();
      if (remaining <= 0 || delay > remaining) {
        // The next poll would fall outside the window: close it on time.
        timer = setTimeout(() => finish('expired'), Math.max(remaining, 0));
        return;
      }
      timer = setTimeout(tick, delay); // eslint-disable-line no-use-before-define
    };

    const tick = async () => {
      timer = null;
      if (!open || cancelled) return;
      attempt += 1;
      if (!isHidden() && !inflight) {
        const outcome = await request();
        if (cancelled || !open) return;
        if (!unresolved.size) { finish('settled'); return; }
        if (outcome.terminal) { finish('expired'); return; }
      }
      planNext();
    };

    const onVisible = () => {
      if (!open || cancelled || isHidden() || inflight) return;
      clear();
      tick();
    };
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);

    // Manual "Check again": one immediate request, no new schedule.
    manualRef.current = async () => {
      if (cancelled || inflight || !unresolved.size) return;
      setChecking(true);
      setCheckFailed(false);
      const outcome = await request();
      if (cancelled) return;
      setChecking(false);
      if (!outcome.ok) setCheckFailed(true);
      if (!unresolved.size) setStatus('settled');
    };

    planNext();

    return () => {
      cancelled = true;
      open = false;
      clear();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
      controller?.abort();
      manualRef.current = null;
    };
  }, [key, organisationId, groupSid]);

  const checkAgain = useCallback(() => { manualRef.current?.(); }, []);

  return {
    results, status, checkAgain, checking, checkFailed,
  };
}
